import { Accordion, AccordionSummary, AccordionDetails, Box, Button, DialogActions, DialogContent, DialogTitle, Modal, Paper, Typography, Skeleton, IconButton, Tooltip } from "@mui/material";
import TooltipConditionalDiscount from "./TooltipConditionalDiscount";
import TooltipUnconditionalDiscount from "./TooltipUncoditionalDiscount";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TooltipNFSeStatus from "./TooltipNFSeStatus";
import FiscalInfosTable from "./FiscalInfosTable";
import { NFSE_STATUS_LABEL } from "../../../../helpers/nfseHelpers";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { formatDateTime } from "../../../../helpers/dayjsConfig";
import SuccessAlertSnackbar from "../../../../../components/shared/snackbars/SuccessAlertSnackbar";
import { useMemo, useState, useEffect } from "react";
import { formatCurrency } from "../../../../helpers/financialHelpers";
import { getFiscalServicePDF, getFiscalServiceXML } from "../../../../api/fiscal";
import ErrorBox from "../../../../../components/shared/boxes/ErrorBox";
import { formatTextToLines } from "../../../../helpers/textHelpers";

const NFSeShowModal = ({ open, onClose, invoice, fiscalService, fiscalConfigData, onEmitAgain }) => {
    const [openCopySnackbar, setOpenCopySnackbar] = useState(false);
    const [apiErrorMessage, setApiErrorMessage] = useState("");
    const [showErrorBox, setShowErrorBox] = useState(true);
    const loadingInvoiceData = !invoice;
    const getFiscalStatusLabel = (status) => {
        return NFSE_STATUS_LABEL[status] || status || "-";
    };

    const modalOnClose = () => {
        onClose();
    };

    const sendAgainIsAllowed = fiscalService?.status && ["rejected", "interrupted", "denied"].includes(fiscalService.status);
    const rejectedReason = sendAgainIsAllowed ? fiscalService?.last_integration_message : null;
    const errorMessageToShow = showErrorBox && (apiErrorMessage || rejectedReason);
    const NFSeIsDone = fiscalService?.status && fiscalService.status === "done";
    const moreInfoLines = useMemo(() =>
        formatTextToLines(fiscalService?.more_info),
        [fiscalService?.more_info]
    );

    const fetchFiscalServiceXML = async () => {
        try {
            const blob = await getFiscalServiceXML(fiscalService.id);

            const url = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.download = `nfse-${invoice.id}.xml`;

            document.body.appendChild(link);
            link.click();

            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            setApiErrorMessage(error.message);
        }
    };

    const fetchFiscalServicePDF = async () => {
        try {
            const blob = await getFiscalServicePDF(fiscalService.id);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `nfse-${invoice.id}.pdf`;

            document.body.appendChild(link);
            link.click();

            link.remove();
            window.URL.revokeObjectURL(url);;
        } catch (error) {
            setApiErrorMessage(error.message)
        }
    };

    useEffect(() => {
        if (open) {
            setShowErrorBox(true);
        }
    }, [open]);

    return (
        <>
            <Modal open={open} onClose={modalOnClose} sx={{ height: '100vh', width: '100vw', display: 'flex', overflow: 'auto' }}>
                <Paper
                    elevation={16}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        p: '1rem',
                        borderRadius: '0.25rem',
                        maxWidth: '38rem',
                        mx: "auto",
                        my: "auto"
                    }}
                >

                    <DialogTitle
                        sx={{
                            p: '1rem 1.5rem',
                            textAlign: 'center',
                        }}>
                        <Typography variant="h6">
                            Visualizar Nota Fiscal de Serviço (NFS-e)
                        </Typography>
                    </DialogTitle>
                    <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: '2.5rem' }}>
                        {errorMessageToShow && (
                            <ErrorBox
                                errorMessage={errorMessageToShow}
                                onClose={() => {
                                    setApiErrorMessage("");
                                    setShowErrorBox(false);
                                }}

                            />
                        )}
                        <Box sx={{ display: 'flex', gap: '5rem' }}>
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography variant="h7"> Status </Typography>
                                    <TooltipNFSeStatus />
                                </Box>
                                <Typography> {getFiscalStatusLabel(fiscalService?.status)} </Typography>
                            </Box>
                            {NFSeIsDone && (
                                <Box>
                                    <Typography variant="h7"> Data da emissão da NFS-e </Typography>
                                    <Typography>
                                        {formatDateTime(fiscalService?.issued_at) || '-'}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                        {NFSeIsDone && (
                            <Box>
                                <Typography variant="h7"> Chave de acesso </Typography>

                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        maxWidth: '26.5rem',
                                        gap: '0.5rem',
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            flex: 1,
                                        }}
                                    >
                                        {fiscalService?.access_key || '-'}
                                    </Typography>

                                    {fiscalService?.access_key && (
                                        <Tooltip title="Copiar">
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    navigator.clipboard.writeText(fiscalService.access_key);
                                                    setOpenCopySnackbar(true);
                                                }}
                                            >
                                                <ContentCopyIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                            </Box>
                        )}
                        <Box sx={{ display: 'flex', gap: '2.5rem' }}>
                            <Box>
                                <Typography variant="h7"> Número da nota </Typography>
                                <Typography> {fiscalService?.fiscal_number || '-'} </Typography>
                            </Box>
                            <Box>
                                <Typography variant="h7"> Série </Typography>
                                <Typography> {fiscalService?.dps_rps_series || '-'} </Typography>
                            </Box>
                            <Box>
                                <Typography variant="h7"> RPS </Typography>
                                <Typography> {fiscalService?.dps_rps_number || '-'} </Typography>
                            </Box>
                        </Box>
                        {NFSeIsDone && (
                            <Box sx={{ display: 'flex', gap: '1.2rem' }}>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    loadingPosition="start"
                                    size='large'
                                    onClick={fetchFiscalServicePDF}
                                >
                                    Baixar PDF
                                </Button>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    loadingPosition="start"
                                    size='large'
                                    onClick={fetchFiscalServiceXML}
                                >
                                    Baixar XML
                                </Button>
                            </Box>
                        )}
                        {sendAgainIsAllowed && (
                            <Box>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    loadingPosition="start"
                                    size='large'
                                    onClick={onEmitAgain}
                                >
                                    Emitir novamente
                                </Button>
                            </Box>
                        )}
                        <Box>
                            <Typography variant="h7"> Cliente (Tomador) </Typography>
                            {loadingInvoiceData ? <Skeleton width={80} /> : <Typography>{invoice?.customer.name}</Typography>}
                        </Box>
                        <Box>
                            <Box>
                                <Typography variant="h7"> Discriminação do serviço <Typography variant='caption' sx={{ color: 'error.main' }}><b>*</b></Typography></Typography>
                            </Box>
                            <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                                {formatTextToLines(fiscalService?.description).map((line, i) => (
                                    <span key={i}>
                                        {line}
                                        <br />
                                    </span>
                                ))}
                            </Typography>
                        </Box>
                        <Box>
                            <Box>
                                <Typography variant="h7"> Valor do serviço <Typography variant='caption' sx={{ color: 'error.main' }}><b>*</b></Typography></Typography>
                            </Box>
                            <Typography> {formatCurrency(fiscalService?.service_value || '')} </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: '1.5rem' }}>
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography variant="h7"> Desconto incondicionado </Typography>
                                    <TooltipUnconditionalDiscount />
                                </Box>
                                <Typography> {formatCurrency(fiscalService?.unconditional_discount_value || '0,00')} </Typography>
                            </Box>
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography variant="h7"> Desconto condicionado </Typography>
                                    <TooltipConditionalDiscount />
                                </Box>
                                <Typography> {formatCurrency(fiscalService?.conditional_discount_value || '0,00')} </Typography>
                            </Box>
                        </Box>
                        <Box>
                            <Box>
                                <Typography variant="h7"> Configuração fiscal do serviço <Typography variant='caption' sx={{ color: 'error.main' }}><b>*</b></Typography></Typography>
                            </Box>
                            <Typography> {fiscalConfigData?.name || '-'} </Typography>
                        </Box>
                        <Box>
                            <Typography variant="h7"> Observações adicionais </Typography>
                            <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                                {moreInfoLines.length
                                    ? moreInfoLines.map((line, i) => (
                                        <span key={i}>
                                            {line}
                                            <br />
                                        </span>
                                    ))
                                    : '-'}
                            </Typography>
                        </Box>
                        <Accordion
                            disableGutters
                            sx={{
                                backgroundColor: "transparent",
                                boxShadow: "none",
                                borderTop: "1px solid",
                                borderBottom: "1px solid",
                                borderColor: "divider",
                                "&:before": {
                                    display: "none",
                                },
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{
                                    px: 0,
                                    minHeight: "48px",
                                    "& .MuiAccordionSummary-content": {
                                        margin: 0,
                                    },
                                }}
                            >
                                <Typography variant="body1"> <b>Detalhes de Tributação e Alíquotas</b></Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ px: 0, pt: 1, pb: 0 }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2rem' }} >
                                    <Box>
                                        <Typography variant="body1">Os valores abaixo são um resumo da configuração fiscal aplicada.</Typography>
                                        <Typography
                                            variant="body1"
                                            onClick={() => window.open("/fiscal/settings", "_blank")}
                                            sx={{
                                                color: "success.main",
                                                textDecoration: "underline",
                                                cursor: "pointer",
                                            }}
                                        >
                                            Acessar as Configurações fiscais de Serviço (NFS-e)
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <FiscalInfosTable fiscalData={fiscalService?.config_payload} />
                                    </Box>
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    </DialogContent>
                    <DialogActions sx={{ display: 'flex' }}>
                        <Button
                            variant="cancelDialog"
                            color="secondary"
                            onClick={modalOnClose}
                        >
                            Fechar
                        </Button>
                    </DialogActions>
                    <SuccessAlertSnackbar
                        open={openCopySnackbar}
                        onClose={() => setOpenCopySnackbar(false)}
                        autoHideDuration={5000}
                        message="Copiado!"
                        vertical="bottom"
                        horizontal="center"
                    />
                </Paper>

            </Modal>

        </>
    )
}

export default NFSeShowModal;